import {
  formatterCurrency,
  formatterNumber,
  formatterPercentage,
  formatterDate,
} from '../utils/formatter';
import Big from 'big.js';

const MAX_NUMBER_DAYS_AGGREGATED_STATS = 50;

let filterAssetsSymbolValues = null;

function setAssets() {
  if (!filterAssetsSymbolValues) {
    filterAssetsSymbolValues = JSON.parse(localStorage.getItem("assets"));
    filterAssetsSymbolValues = Object.keys(filterAssetsSymbolValues).map((key) => {
      const asset = filterAssetsSymbolValues[key];
      return { key: asset.id, value: asset.id, text: asset.symbol };
    });
  }
}

export const resources = {
  client: {
    name: 'client',
    link: '/clients',
    filters: ['clientId', 'createdAt'],
    includeCols: ['id', 'createdAt', 'name', 'email', 'audience.0'],
    sort: ['createdAt', 'id'],
    actions: {
      add: {
        itemProperties: [
          {
            name: 'name',
          },
          {
            name: 'email',
          },
          {
            name: 'audience',
            defaultValue: 'admin',
            disabled: true,
            isArray: true,
          },
          {
            name: 'password',
            type: 'password',
          },
        ],
      },
    },
    audiences: ['admin'],
  },

  user: {
    name: 'user',
    link: '/users',
    filters: ['userId', 'idNumber', 'email', 'blocked', 'createdAt'],
    includeCols: ['id', 'createdAt', 'idNumber', 'email', 'blocked'],
    sort: ['createdAt', 'id'],
    actions: {},
    audiences: ['admin'],
  },

  wallet: {
    name: 'wallet',
    link: '/users/*/wallets',
    filters: ['walletId', 'userId', 'assetSymbol', 'assetName', 'createdAt'],
    includeCols: [
      'id',
      'createdAt',
      'user.id',
      'asset.id',
      'asset.symbol',
      'asset.name',
      {
        name: 'balance',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      {
        name: 'balanceInEur',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
    ],
    sort: ['createdAt', 'id'],
    actions: {},
    audiences: ['admin'],
  },

  transaction: {
    name: 'transaction',
    link: '/users/*/wallets/*/transactions',
    filters: [
      'transactionId',
      'userId',
      'walletId',
      'address',
      'assetSymbol',
      'movement',
      'status',
      'createdAt',
    ],
    includeCols: [
      'id',
      'createdAt',
      'sender.walletId',
      'sender.userId',
      'receiver.walletId',
      'receiver.userId',
      'movement',
      'asset.symbol',
      {
        name: 'amount',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      'status',
      {
        name: 'amountInEur',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
    ],
    sort: ['createdAt', 'id'],
    actions: {},
    audiences: ['admin'],
  },

  withdrawal: {
    name: 'withdrawal',
    link: '/users/*/wallets/*/withdrawals',
    filters: ['withdrawalId', 'userId', 'walletId', 'status', 'assetId'],
    includeCols: [
      'id',
      'createdAt',
      'walletId',
      'receiverAddress',
      'asset.symbol',
      'assetId',
      {
        name: 'amount',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      'status',
    ],
    sort: ['createdAt', 'id', 'walletId', 'assetId'],
    actions: {},
    audiences: ['admin'],
  },

  purchase: {
    name: 'purchase',
    link: '/users/*/wallets/*/purchases',
    filters: [
      'purchaseId',
      'userId',
      'walletId',
      'assetId',
      'status',
      'operation',
      'createdAt',
    ],
    includeCols: [
      'id',
      'createdAt',
      'userId',
      'walletId',
      'assetId',
      'asset.symbol',
      'operation',
      'status',
      {
        name: 'amount',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      {
        name: 'rate',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 5,
            minDecimals: 1,
          },
        },
      },
      {
        name: 'amountInEur',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
      {
        name: 'fee',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
    ],
    sort: ['createdAt', 'id', 'userId', 'walletId', 'assetId'],
    actions: {},
    audiences: ['admin'],
  },

  otcOrder: {
    name: 'otcOrder',
    link: '/users/*/wallets/*/trade/otc-order',
    filters: [
      'otcOrderId',
      'userId',
      'walletId',
      'assetId',
      'otcStatus',
      'operation',
      'createdAt',
    ],
    includeCols: [
      'id',
      'createdAt',
      'userId',
      'walletId',
      'assetId',
      'asset.symbol',
      'operation',
      'status',
      {
        name: 'amount',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      {
        name: 'rate',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 5,
            minDecimals: 1,
          },
        },
      },
      {
        name: 'amountInEur',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
      {
        name: 'fee',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
    ],
    sort: ['createdAt', 'id', 'userId', 'walletId', 'assetId'],
    actions: {},
    audiences: ['admin'],
  },

  asset: {
    name: 'asset',
    link: '/assets',
    filters: ['assetId', 'symbol', 'name'],
    includeCols: [
      'id',
      'symbol',
      'name',
      'rate.id',
      {
        name: 'rate.EUR',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            maxDecimals: 5,
            minDecimals: 1,
          },
        },
      },
      'marketData.id',
    ],
    sort: ['symbol', 'name', 'id'],
    actions: {},
  },

  rate: {
    name: 'rate',
    link: '/assets/*/rates',
    filters: ['rateId', 'assetId', 'assetSymbol', 'createdAt'],
    includeCols: [
      'id',
      'createdAt',
      'asset.id',
      'asset.name',
      {
        name: 'EUR',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            maxDecimals: 5,
            minDecimals: 1,
          },
        },
      },
    ],
    sort: ['createdAt', 'id'],
    actions: {},
  },

  marketData: {
    name: 'marketData',
    title: 'Market Data',
    link: '/assets/*/market-data',
    filters: ['marketDataId', 'assetSymbol', 'createdAt'],
    includeCols: [
      'id',
      'createdAt',
      'asset.symbol',
      {
        name: 'volume24h',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'percentChange24h',
        formatter: {
          fn: formatterPercentage,
          parameters: {
            decimals: 3,
            convertToHundred: false,
          },
        },
      },
      {
        name: 'percentChange7d',
        formatter: {
          fn: formatterPercentage,
          parameters: {
            decimals: 3,
            convertToHundred: false,
          },
        },
      },
      {
        name: 'circulatingSupply',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'totalSupply',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'maxSupply',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
    ],
    sort: ['createdAt', 'id'],
    actions: {},
  },

  userStat: {
    name: 'userStat',
    title: 'Users',
    link: '/stats/user',
    filters: ['date'],
    includeCols: [
      {
        name: 'date',
        formatter: {
          fn: formatterDate,
          parameters: {
          },
        },
      },
      {
        name: 'total',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'new',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'activeTotal',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'activeNew',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
    ],
    sort: ['date'],
    actions: {},
    views: ['Table', 'Chart'],
  },

  walletStat: {
    name: 'walletStat',
    title: 'Wallets',
    link: '/stats/wallet',
    filters: [
      {
        type: 'select',
        label: 'assetSymbol',
        queryParameter: 'asset',
        options: () => {
          setAssets();
          return filterAssetsSymbolValues;
        }
      },
      'date'],
    includeCols: [
      {
        name: 'date',
        formatter: {
          fn: formatterDate,
          parameters: {
          },
        },
      },
      'asset.symbol',
      {
        name: 'total',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'new',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'withPositiveBalance',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'highestBalance',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      {
        name: 'meanValue',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      {
        name: 'modeValue',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      {
        name: 'medianValue',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      {
        name: 'varianceValue',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
      {
        name: 'walletsToZero',
        formatter: {
          fn: formatterNumber,
          parameters: {},
        },
      },
    ],
    sort: ['date'],
    actions: {},
  },

  walletGlobalStat: {
    name: 'walletGlobalStat',
    title: 'AUM',
    link: '/stats/wallet-global',
    filters: [
      {
        type: 'select',
        label: 'assetSymbol',
        queryParameter: 'asset',
        options: () => {
          setAssets();
          return filterAssetsSymbolValues;
        }
      },
      'date'],
    includeCols: [
      {
        name: 'date',
        formatter: {
          fn: formatterDate,
          parameters: {
          },
        },
      },
      {
        name: 'aum',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 2,
          },
        },
      },
      {
        name: 'aumEUR',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
      'options',
    ],
    sort: ['date'],
    actions: {},
    views: ['Table', 'Chart'],
    maxDays: MAX_NUMBER_DAYS_AGGREGATED_STATS,
    filtersNote: `A maximum of ${MAX_NUMBER_DAYS_AGGREGATED_STATS} days will be displayed`,
    isAggregateStats: true,
    aggregateFunction: function (data) {
      const mapAggregated = {}
      let count = 0
      data.forEach(
        (row) => {
          const rowTotals = mapAggregated[row['date']];
          if (rowTotals) {
            rowTotals.totals.aumEUR = new Big(rowTotals.totals.aumEUR).add(new Big(row.aumEUR ? row.aumEUR : 0)).toString();
            rowTotals.totals.aum = new Big(rowTotals.totals.aum).add(new Big(row.aum ? row.aum : 0)).toString();
            rowTotals.rows.push(row)
          }
          else {
            mapAggregated[row['date']] = {
              index: count,
              totals: {
                createdAt: row.createdAt,
                date: row.date,
                options: 'Details',
                aumEUR: row.aumEUR ? row.aumEUR : 0,
                aum: row.aum ? row.aum : 0,
              },
              rows: [row]
            }
            count++;
          }
        },
      );
      return { data: Object.values(mapAggregated).map(a => a.totals), rows: mapAggregated };
    }
  },

  walletGlobalStatPerAsset: {
    name: 'walletGlobalStatPerAsset',
    title: 'AUM Per Asset',
    link: '/stats/wallet-global',
    filters: [
      {
        type: 'select',
        label: 'assetSymbol',
        queryParameter: 'asset',
        options: () => {
          setAssets();
          return filterAssetsSymbolValues;
        }
      },
      'date'],
    includeCols: [
      {
        name: 'date',
        formatter: {
          fn: formatterDate,
          parameters: {
          },
        },
      },
      'asset.symbol',
      {
        name: 'Aum',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 2,
          },
        },
      },
      {
        name: 'rateEUR',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            maxDecimals: 5,
            minDecimals: 1,
          },
        },
      },
      {
        name: 'aumEUR',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      }
    ],
    sort: ['date'],
    actions: {},
    views: ['Table'],
  },

  transactionStat: {
    name: 'transactionStat',
    title: 'Transactions',
    link: '/stats/transaction',
    filters: [
      {
        type: 'select',
        label: 'assetSymbol',
        queryParameter: 'asset',
        options: () => {
          setAssets();
          return filterAssetsSymbolValues;
        }
      },
      'movement', 'date'],
    includeCols: [
      {
        name: 'date',
        formatter: {
          fn: formatterDate,
          parameters: {
          },
        },
      },
      {
        name: 'totalTransactions',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'totalAmount',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 5,
            minDecimals: 1,
          },
        },
      },
      {
        name: 'totalAmountEUR',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
      'options',
    ],
    sort: ['date'],
    actions: {},
    views: ['Table', 'Chart'],
    maxDays: MAX_NUMBER_DAYS_AGGREGATED_STATS,
    filtersNote: `A maximum of ${MAX_NUMBER_DAYS_AGGREGATED_STATS} days will be displayed`,
    isAggregateStats: true,
    aggregateFunction: function (data) {
      const mapAggregated = {}
      let count = 0
      data.forEach(
        (row) => {
          const rowTotals = mapAggregated[row['date']];
          if (rowTotals) {
            rowTotals.totals.totalAmount = new Big(rowTotals.totals.totalAmount).add(new Big(row.totalAmount ? row.totalAmount : 0)).toString();
            rowTotals.totals.totalTransactions = new Big(rowTotals.totals.totalTransactions).add(new Big(row.totalTransactions ? row.totalTransactions : 0)).toString();
            rowTotals.totals.totalAmountEUR = new Big(rowTotals.totals.totalAmountEUR).add(new Big(row.amountEur ? row.amountEur : 0)).toString();
            rowTotals.rows.push(row)
          }
          else {
            mapAggregated[row['date']] = {
              index: count,
              totals: {
                createdAt: row.createdAt,
                date: row.date,
                options: 'Details',
                totalAmount: row.totalAmount ? row.totalAmount : 0,
                totalTransactions: row.totalTransactions ? row.totalTransactions : 0,
                totalAmountEUR: row.amountEur ? row.amountEur : 0
              },
              rows: [row]
            }
            count++;
          }
        },
      );
      return { data: Object.values(mapAggregated).map(a => a.totals), rows: mapAggregated };
    }
  },

  transactionStatPerAsset: {
    name: 'transactionStatPerAsset',
    title: 'Transactions Per Asset',
    link: '/stats/transaction',
    filters: [
      {
        type: 'select',
        label: 'assetSymbol',
        queryParameter: 'asset',
        options: () => {
          setAssets();
          return filterAssetsSymbolValues;
        }
      },
      'movement', 'date'],
    includeCols: [
      {
        name: 'date',
        formatter: {
          fn: formatterDate,
          parameters: {
          },
        },
      },
      'asset.symbol',
      'movement',
      {
        name: 'totalTransactions',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'uniqueUsers',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'totalAmount',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 5,
            minDecimals: 1,
          },
        },
      },
      // {
      //   name: 'rateEUR',
      //   formatter: {
      //     fn: formatterCurrency,
      //     parameters: {
      //       maxDecimals: 5,
      //       minDecimals: 1,
      //     },
      //   },
      // },
      {
        name: 'amountEur',
        formatter: {
          fn: formatterCurrency,
          parameters: {
            decimals: 2,
          },
        },
      },
      {
        name: 'max',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'meanValue',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'varianceValue',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'modeValue',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      }
    ],
    sort: ['date'],
    actions: {},
    views: ['Table'],
  },

  purchaseStat: {
    name: 'purchaseStat',
    title: 'Purchases',
    link: '/stats/purchase',
    filters: [
      {
        type: 'select',
        label: 'assetSymbol',
        queryParameter: 'asset',
        options: () => {
          setAssets();
          return filterAssetsSymbolValues;
        }
      }
      , 'date'],
    includeCols: [
      {
        name: 'date',
        formatter: {
          fn: formatterDate,
          parameters: {
          },
        },
      },
      'asset.symbol',
      'operation',
      {
        name: 'numberOfOperations',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'totalAmount',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'totalFees',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'totalNetAmount',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'highestAmount',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'distinctUsers',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'numberOfPendingOperations',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'numberOfRejectedOperations',
        formatter: {
          fn: formatterNumber,
          parameters: {
            decimals: 0,
          },
        },
      },
      {
        name: 'meanValue',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'modeValue',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'medianValue',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
      {
        name: 'varianceValue',
        formatter: {
          fn: formatterNumber,
          parameters: {
            maxDecimals: 4,
            minDecimals: 0,
          },
        },
      },
    ],
    sort: ['date'],
    actions: {},
  },
};
